import { useState, useEffect } from "react";
import { Shield } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";

const MESSAGES = [
  "Scanning vulnerability databases…",
  "Analyzing security advisories…",
  "Checking exploit status…",
  "Cross-referencing platform data…",
  "Almost there…",
];

interface Props {
  loadedCount?: number;
  totalCount?: number;
}

export function VulnerabilityLoadingSkeleton({ loadedCount = 0, totalCount = 6 }: Props) {
  const [msgIdx, setMsgIdx] = useState(0);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => setMsgIdx((i) => (i + 1) % MESSAGES.length), 2000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setProgress((p) => (p >= 90 ? 90 : p + Math.random() * 15));
    }, 400);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Hero section */}
      <div className="flex flex-col items-center justify-center pt-8 pb-4 space-y-5">
        <div className="relative">
          <div className="absolute inset-0 rounded-full bg-primary/20 animate-ping" style={{ animationDuration: "2s" }} />
          <div className="relative p-4 rounded-full bg-primary/10 border border-primary/20">
            <Shield className="h-10 w-10 text-primary animate-pulse" />
            {/* Scan line */}
            <div
              className="absolute inset-0 rounded-full overflow-hidden pointer-events-none"
              aria-hidden
            >
              <div
                className="absolute left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-primary/60 to-transparent"
                style={{
                  animation: "scanSweep 2s ease-in-out infinite",
                }}
              />
            </div>
          </div>
        </div>

        <p className="text-sm font-medium text-muted-foreground transition-all duration-500">
          {MESSAGES[msgIdx]}
        </p>

        <div className="w-64">
          <Progress value={progress} className="h-1.5" />
        </div>
      </div>

      {/* Skeleton stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className="rounded-2xl backdrop-blur-md bg-card/40 border border-border/30 p-6 space-y-3"
            style={{ animationDelay: `${i * 100}ms` }}
          >
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-8 w-16" />
            <Skeleton className="h-3 w-32" />
          </div>
        ))}
      </div>

      {/* Skeleton chart area */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-2xl backdrop-blur-md bg-card/40 border border-border/30 p-6 space-y-4">
          <Skeleton className="h-5 w-40" />
          <Skeleton className="h-48 w-full rounded-xl" />
        </div>
        <div className="rounded-2xl backdrop-blur-md bg-card/40 border border-border/30 p-6 space-y-4">
          <Skeleton className="h-5 w-36" />
          <Skeleton className="h-48 w-full rounded-xl" />
        </div>
      </div>

      <style>{`
        @keyframes scanSweep {
          0%, 100% { top: 0; }
          50% { top: 100%; }
        }
      `}</style>
    </div>
  );
}
